'use client';
import { createContext, useContext, useEffect, useState } from 'react';
import { getTenantConfig, getSupabaseClient } from '@/lib/supabase/auth';

interface TenantBrand {
  nome_breve: string;
  colore_primario: string;
  colore_secondario: string;
  colore_testo: string;
  logo_url: string | null;
}

interface TenantToast {
  testo: string;
  attivo: boolean;
  scade_il: string | null;
}

interface TenantContextType {
  brand: TenantBrand | null;
  toast: TenantToast | null;
  loading: boolean;
}

const defaultBrand: TenantBrand = {
  nome_breve: '3940',
  colore_primario: '#f97316',
  colore_secondario: '#1e293b',
  colore_testo: '#ffffff',
  logo_url: null,
};

const TenantContext = createContext<TenantContextType>({
  brand: defaultBrand,
  toast: null,
  loading: true,
});

export function TenantProvider({ children }: { children: React.ReactNode }) {
  const [brand, setBrand] = useState<TenantBrand | null>(defaultBrand);
  const [toast, setToast] = useState<TenantToast | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (brand) {
      document.documentElement.style.setProperty('--color-primary', brand.colore_primario);
      document.documentElement.style.setProperty('--color-secondary', brand.colore_secondario);
      document.documentElement.style.setProperty('--color-text-on-primary', brand.colore_testo);
      document.title = brand.nome_breve && brand.nome_breve !== '3940'
        ? `${brand.nome_breve} | Preparazione concorsi`
        : '3940 | Preparazione concorsi';
    }
  }, [brand]);

  useEffect(() => {
    const supabase = getSupabaseClient();

    const loadTenantFromSlug = async () => {
      const params = new URLSearchParams(window.location.search);
      const slug = params.get('tenant') ?? sessionStorage.getItem('tenant_slug');
      if (!slug) return false;
      sessionStorage.setItem('tenant_slug', slug);
      const res = await fetch(`/api/tenant/by-slug?slug=${slug}`);
      if (!res.ok) return false;
      const data = await res.json();
      if (data.brand) {
        setBrand(data.brand);
        if (data.toast) setToast(data.toast);
        return true;
      }
      return false;
    };

    const loadTenantFromSession = async () => {
      const config = await getTenantConfig();
      if (config) {
        if (config.brand) setBrand(config.brand);
        if (config.toast) setToast(config.toast);
      }
      setLoading(false);
    };

    const init = async () => {
      const foundSlug = await loadTenantFromSlug();
      if (!foundSlug) {
        await loadTenantFromSession();
      } else {
        setLoading(false);
      }
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (session?.user) {
          loadTenantFromSession();
        } else {
          loadTenantFromSlug().then(found => {
            if (!found) {
              const slugSalvato = sessionStorage.getItem('tenant_slug');
              if (!slugSalvato) {
                setBrand(defaultBrand);
                setToast(null);
              }
              setLoading(false);
            }
          });
        }
      }
    );

    init();
    return () => { subscription.unsubscribe(); };
  }, []);

  return (
    <TenantContext.Provider value={{ brand, toast, loading }}>
      {children}
    </TenantContext.Provider>
  );
}

export function useTenant() {
  return useContext(TenantContext);
}